import React, { useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import InputMaskAlternative from '../../../../components/InputMaskAlternative';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { useCurrentCallback } from '../../../../lib/use-current-effect';
import {
  confirmWhatsappLink,
  requestWhatsappLink,
} from '../../../../services/patient/PatientWhatsappLink';

import { Container, Field, SendButton, SkipButton } from './styles';

interface WhatsappLinkProps {
  /** A sessão de paciente que a ativação devolveu. */
  token: string;
  onFinish: (dados: { whatsappLinked: boolean }) => void;
}

interface NumberFormProps {
  whatsappNumber: string;
}

interface CodeFormProps {
  code: string;
}

/**
 * Passo 3, **opcional**: vincula o WhatsApp do paciente (tarefa 0.34).
 *
 * ⚠️ **É o único caminho que registra o paciente no `cinco_chat` hoje.** O vínculo era
 * exclusivo do assistente do `cinco-b2c`, que não está publicado — sem ele o paciente nasce
 * com conta no `cinco-back` e invisível para a CintIA. As rotas chamadas aqui são as mesmas
 * do aplicativo; o que muda é quem carrega a sessão.
 *
 * ⚠️ **Opcional de propósito, e "Vincular depois" fica visível nos dois estados.** A conta já
 * está criada e o termo já está assinado quando esta tela aparece: uma indisponibilidade do
 * chat ou um número errado não podem virar impedimento para concluir. O `link-request` falha
 * na hora quando o `chat_server` está sem credencial de WhatsApp, e é para isso que o botão
 * existe.
 */
const WhatsappLink: React.FC<WhatsappLinkProps> = ({ token, onFinish }) => {
  const numberFormRef = useRef<FormHandles>(null);
  const codeFormRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const enviaCodigo = useCurrentCallback(
    (isCurrent) => async (numero: string) => {
      setLoading(true);

      try {
        await requestWhatsappLink({ token, whatsappNumber: numero });

        if (isCurrent()) {
          setWhatsappNumber(numero);
          setLoading(false);
          toast.success('Código enviado pelo WhatsApp.');
        }
      } catch (err) {
        if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Não foi possível enviar o código agora.');
        }

        setLoading(false);
      }
    },
    [token],
  );

  const handleSubmitNumber = useCurrentCallback(
    () => async (data: NumberFormProps) => {
      numberFormRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          whatsappNumber: Yup.string()
            // A máscara é `(99) 9 9999-9999`; com DDD são 16 caracteres. O back aceita
            // qualquer máscara e normaliza para E.164, então o que se valida aqui é só se
            // a pessoa terminou de digitar.
            .min(16, 'Digite o número com DDD')
            .required('O número é obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          numberFormRef.current?.setErrors(getValidationErrors(err));
        }

        return;
      }

      await enviaCodigo(data.whatsappNumber);
    },
    [enviaCodigo],
  );

  const handleSubmitCode = useCurrentCallback(
    (isCurrent) => async (data: CodeFormProps) => {
      codeFormRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          code: Yup.string()
            .matches(/^\d{6}$/, 'O código tem 6 dígitos')
            .required('O código é obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          codeFormRef.current?.setErrors(getValidationErrors(err));
        }

        return;
      }

      setLoading(true);

      try {
        await confirmWhatsappLink({ token, code: data.code });

        if (isCurrent()) {
          setLoading(false);
          onFinish({ whatsappLinked: true });
        }
      } catch (err) {
        if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Não foi possível confirmar o código agora.');
        }

        setLoading(false);
      }
    },
    [onFinish, token],
  );

  const handleSkip = (): void => {
    onFinish({ whatsappLinked: false });
  };

  const handleResend = (): void => {
    enviaCodigo(whatsappNumber);
  };

  if (!whatsappNumber) {
    return (
      <Container>
        <h1>Acompanhamento pelo WhatsApp</h1>
        <h2>
          Informe o seu número de WhatsApp para receber o acompanhamento da
          Cinco por lá.
        </h2>
        {/**
         * ⚠️ **Aqui dizia "você pode fazer isso depois, pelo aplicativo" (0.35).** Ficou
         * contraditório em dois sentidos ao mesmo tempo: o app da loja é o
         * **descontinuado**, e desde a 0.35 o "depois" é justamente esta plataforma —
         * `/patient/whatsapp`. Percorrer a tela é o que mostrou a frase se contradizendo.
         */}
        <p>
          É por esse número que a equipe fala com você. Se preferir deixar para
          depois, é só voltar em saudecinco.com/patient/whatsapp e informar o seu
          e-mail.
        </p>
        {/**
         * ⚠️ **O `key` não é decoração — sem ele o campo do código nasce preenchido.** As
         * duas telas deste passo renderizam a mesma árvore (`Form` → `Field` →
         * `InputMaskAlternative`) na mesma posição, então o React **reaproveita o mesmo
         * `<input>`** ao trocar de uma para a outra e o valor digitado sobrevive: a máscara
         * `999999` truncava o telefone e o campo aparecia com os 6 primeiros dígitos dele,
         * com cara de código válido. Medido percorrendo a tela no navegador. `key` distinto
         * força a remontagem, e cada campo nasce vazio.
         */}
        <Form key="numero" ref={numberFormRef} onSubmit={handleSubmitNumber}>
          <Field>
            <label>WhatsApp:</label>
            <InputMaskAlternative
              name="whatsappNumber"
              type="text"
              title=""
              placeholder="(00) 0 0000-0000"
              mask="(99) 9 9999-9999"
            />
          </Field>
          <SendButton loading={loading} type="submit">
            Enviar código
          </SendButton>
        </Form>
        <SkipButton type="button" onClick={handleSkip}>
          Vincular depois
        </SkipButton>
      </Container>
    );
  }

  return (
    <Container>
      <h1>Confirme o código</h1>
      <h2>{`Enviamos um código de 6 dígitos por WhatsApp para ${whatsappNumber}.`}</h2>
      <p>O código vale 10 minutos.</p>
      <Form key="codigo" ref={codeFormRef} onSubmit={handleSubmitCode}>
        <Field>
          <label>Código:</label>
          <InputMaskAlternative
            name="code"
            type="text"
            title=""
            placeholder="000000"
            mask="999999"
          />
        </Field>
        <SendButton loading={loading} type="submit">
          Confirmar
        </SendButton>
      </Form>
      <SkipButton type="button" onClick={handleResend}>
        Reenviar código
      </SkipButton>
      <SkipButton type="button" onClick={handleSkip}>
        Vincular depois
      </SkipButton>
    </Container>
  );
};

export default WhatsappLink;
